import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, Eye, EyeOff, Bell, BellOff, KeyRound, X, CalendarClock, CalendarOff, Send } from "lucide-react";
import { apiClient, formatApiError } from "../../lib/api";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
        return iso;
    }
}

export const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [notifyNew, setNotifyNew] = useState(false);
    const [creating, setCreating] = useState(false);
    const [pwUser, setPwUser] = useState(null);
    const [pwValue, setPwValue] = useState("");
    const [settingPw, setSettingPw] = useState(false);

    const setUserPassword = async () => {
        if (pwValue.length < 8) {
            toast.error("Das Passwort muss mindestens 8 Zeichen haben.");
            return;
        }
        setSettingPw(true);
        try {
            await apiClient.post(`/admin/users/${pwUser.id}/password`, { new_password: pwValue });
            toast.success(`Passwort für ${pwUser.email} geändert. Bitte dem Mitarbeiter mitteilen.`);
            setPwUser(null);
            setPwValue("");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Passwortänderung fehlgeschlagen.");
        } finally {
            setSettingPw(false);
        }
    };

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await apiClient.get("/admin/users");
            setUsers(data.items || []);
            setCurrentUserId(data.current_user_id);
        } catch (err) {
            if (err?.response?.status !== 401) {
                toast.error(formatApiError(err?.response?.data?.detail) || "Benutzer konnten nicht geladen werden.");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const createUser = async (e) => {
        e.preventDefault();
        if (!email.trim() || password.length < 8) {
            toast.error("Bitte gültige E-Mail und Passwort (mind. 8 Zeichen) angeben.");
            return;
        }
        setCreating(true);
        try {
            await apiClient.post("/admin/users", { email: email.trim(), password, notify_on_application: notifyNew });
            toast.success(`Benutzer ${email.trim()} angelegt.`);
            setEmail("");
            setPassword("");
            setNotifyNew(false);
            fetchUsers();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Benutzer konnte nicht angelegt werden.");
        } finally {
            setCreating(false);
        }
    };

    const toggleNotify = async (user) => {
        const next = !user.notify_on_application;
        try {
            await apiClient.patch(`/admin/users/${user.id}`, { notify_on_application: next });
            setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, notify_on_application: next } : u)));
            toast.success(
                next
                    ? `${user.email} erhält jetzt Benachrichtigungen bei neuen Bewerbungen.`
                    : `Benachrichtigungen für ${user.email} deaktiviert.`
            );
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Änderung fehlgeschlagen.");
        }
    };

    const toggleWeekly = async (user) => {
        const next = !user.weekly_report;
        try {
            await apiClient.patch(`/admin/users/${user.id}`, { weekly_report: next });
            setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, weekly_report: next } : u)));
            toast.success(
                next
                    ? `${user.email} erhält jetzt montags den Wochenbericht.`
                    : `Wochenbericht für ${user.email} deaktiviert.`
            );
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Änderung fehlgeschlagen.");
        }
    };

    const [sendingReport, setSendingReport] = useState(false);
    const sendReportNow = async () => {
        setSendingReport(true);
        try {
            const { data } = await apiClient.post("/admin/weekly-report/send");
            if (!data.sent?.length) {
                toast.info(data.message || "Kein Benutzer hat den Wochenbericht aktiviert.");
            } else {
                const ok = data.sent.filter((s) => s.ok).length;
                const fail = data.sent.length - ok;
                if (fail) toast.error(`Wochenbericht: ${ok} gesendet, ${fail} fehlgeschlagen.`);
                else toast.success(`Wochenbericht an ${ok} Empfänger gesendet.`);
            }
        } catch {
            toast.error("Wochenbericht konnte nicht gesendet werden.");
        } finally {
            setSendingReport(false);
        }
    };

    const deleteUser = async (user) => {
        if (!window.confirm(`Benutzer ${user.email} wirklich löschen?`)) return;
        try {
            await apiClient.delete(`/admin/users/${user.id}`);
            toast.success("Benutzer gelöscht.");
            fetchUsers();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Löschen fehlgeschlagen.");
        }
    };

    return (
        <div data-testid="user-management" className="space-y-6">
            {/* Create form */}
            <div className="bg-white border border-moebus-border rounded-lg p-4 md:p-6">
                <h2 className="font-heading font-semibold text-lg text-moebus-deep mb-1">
                    Neuen Benutzer anlegen
                </h2>
                <p className="text-sm text-slate-500 mb-4">
                    Der neue Benutzer erhält vollen Admin-Zugriff. Teilen Sie ihm E-Mail und Passwort mit.
                </p>
                <form onSubmit={createUser} className="grid md:grid-cols-12 gap-3">
                    <div className="md:col-span-5">
                        <input
                            data-testid="new-user-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="E-Mail-Adresse"
                            required
                            className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-moebus-green"
                        />
                    </div>
                    <div className="md:col-span-4 relative">
                        <input
                            data-testid="new-user-password"
                            type={showPw ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Passwort (mind. 8 Zeichen)"
                            minLength={8}
                            required
                            className="w-full px-3 py-2.5 pr-10 border border-moebus-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-moebus-green"
                        />
                        <button
                            type="button"
                            data-testid="toggle-password-visibility"
                            onClick={() => setShowPw(!showPw)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>
                    <div className="md:col-span-3">
                        <button
                            data-testid="create-user-btn"
                            type="submit"
                            disabled={creating}
                            className="w-full inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-4 py-2.5 rounded-md text-sm disabled:opacity-60"
                        >
                            <UserPlus size={15} /> {creating ? "Anlegen…" : "Benutzer anlegen"}
                        </button>
                    </div>
                    <div className="md:col-span-12">
                        <label className="flex items-center gap-2.5 cursor-pointer select-none w-fit">
                            <input
                                data-testid="new-user-notify"
                                type="checkbox"
                                checked={notifyNew}
                                onChange={(e) => setNotifyNew(e.target.checked)}
                                className="w-4 h-4 accent-emerald-600"
                            />
                            <span className="text-sm text-slate-700">
                                Erhält Benachrichtigungen per E-Mail bei neuen Bewerbungen
                            </span>
                        </label>
                    </div>
                </form>
            </div>

            {/* Weekly report info */}
            <div className="bg-white border border-moebus-border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <p className="text-sm text-slate-500">
                    Der <strong className="text-moebus-deep">Wochenbericht</strong> wird jeden Montag um 7:00 Uhr an alle
                    Benutzer mit aktiviertem Bericht gesendet (neue Bewerbungen, offene Vorgänge, Erinnerungen).
                </p>
                <button
                    data-testid="send-weekly-report-btn"
                    onClick={sendReportNow}
                    disabled={sendingReport}
                    className="shrink-0 inline-flex items-center gap-2 bg-white border border-moebus-border text-slate-700 px-4 py-2 rounded-md text-sm hover:bg-slate-50 disabled:opacity-60"
                >
                    <Send size={14} /> {sendingReport ? "Sende…" : "Wochenbericht jetzt senden"}
                </button>
            </div>

            {/* User list */}
            <div className="bg-white border border-moebus-border rounded-lg overflow-hidden">
                <table data-testid="users-table" className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-moebus-border">
                        <tr className="text-left">
                            <th className="px-4 py-3 font-medium text-slate-600">E-Mail</th>
                            <th className="px-4 py-3 font-medium text-slate-600">Rolle</th>
                            <th className="px-4 py-3 font-medium text-slate-600">Benachrichtigungen</th>
                            <th className="px-4 py-3 font-medium text-slate-600">Wochenbericht</th>
                            <th className="px-4 py-3 font-medium text-slate-600">Angelegt am</th>
                            <th className="px-4 py-3 font-medium text-slate-600 text-right">Aktion</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={6} className="p-8 text-center text-slate-500">Lade…</td>
                            </tr>
                        )}
                        {!loading && users.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-moebus-border last:border-0 hover:bg-slate-50">
                                <td className="px-4 py-3 font-medium text-moebus-deep">
                                    {u.email}
                                    {u.id === currentUserId && (
                                        <span className="ml-2 text-xs text-emerald-600 font-normal">(Sie)</span>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <span className="inline-flex items-center gap-1.5 text-slate-600">
                                        <Shield size={13} className="text-moebus-green" /> Admin
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <button
                                        data-testid={`toggle-notify-${u.id}`}
                                        onClick={() => toggleNotify(u)}
                                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                            u.notify_on_application
                                                ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                        }`}
                                    >
                                        {u.notify_on_application ? <Bell size={13} /> : <BellOff size={13} />}
                                        {u.notify_on_application ? "Aktiv" : "Aus"}
                                    </button>
                                </td>
                                <td className="px-4 py-3">
                                    <button
                                        data-testid={`toggle-weekly-${u.id}`}
                                        onClick={() => toggleWeekly(u)}
                                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                            u.weekly_report
                                                ? "bg-sky-100 text-sky-800 hover:bg-sky-200"
                                                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                        }`}
                                    >
                                        {u.weekly_report ? <CalendarClock size={13} /> : <CalendarOff size={13} />}
                                        {u.weekly_report ? "Aktiv" : "Aus"}
                                    </button>
                                </td>
                                <td className="px-4 py-3 text-slate-600">{formatDate(u.created_at)}</td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex items-center gap-3">
                                        <button
                                            data-testid={`set-password-${u.id}`}
                                            onClick={() => { setPwUser(u); setPwValue(""); }}
                                            title="Passwort ändern"
                                            className="inline-flex items-center gap-1 text-slate-500 hover:text-moebus-green text-sm font-medium"
                                        >
                                            <KeyRound size={14} /> Passwort
                                        </button>
                                        {u.id !== currentUserId && (
                                            <button
                                                data-testid={`delete-user-${u.id}`}
                                                onClick={() => deleteUser(u)}
                                                className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 text-sm font-medium"
                                            >
                                                <Trash2 size={14} /> Löschen
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Set password dialog */}
            {pwUser && (
                <div
                    data-testid="set-password-dialog"
                    className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setPwUser(null)}
                >
                    <div className="bg-white w-full max-w-md rounded-lg shadow-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-heading font-bold text-lg text-moebus-deep">
                                Passwort ändern
                            </h2>
                            <button onClick={() => setPwUser(null)} className="text-slate-400 hover:text-slate-700">
                                <X size={20} />
                            </button>
                        </div>
                        <p className="text-sm text-slate-500 mb-4">
                            Neues Passwort für <strong className="text-moebus-deep">{pwUser.email}</strong> festlegen.
                            Bitte teilen Sie es dem Mitarbeiter anschließend mit.
                        </p>
                        <input
                            data-testid="set-password-input"
                            type="text"
                            value={pwValue}
                            onChange={(e) => setPwValue(e.target.value)}
                            placeholder="Neues Passwort (mind. 8 Zeichen)"
                            minLength={8}
                            autoFocus
                            className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-moebus-green"
                        />
                        <div className="flex gap-3 mt-4">
                            <button
                                data-testid="set-password-save"
                                onClick={setUserPassword}
                                disabled={settingPw}
                                className="flex-1 inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-5 py-2.5 rounded-md text-sm disabled:opacity-60"
                            >
                                <KeyRound size={14} /> {settingPw ? "Speichern…" : "Passwort speichern"}
                            </button>
                            <button
                                onClick={() => setPwUser(null)}
                                className="inline-flex items-center justify-center bg-white border border-moebus-border text-slate-700 px-5 py-2.5 rounded-md text-sm hover:bg-slate-50"
                            >
                                Abbrechen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
